import React from 'react';
import { useQueryFetchContent } from '../common/hooks/useQueryFetchContent';
import { Spinner } from 'reactstrap';
import { DocumentViewer } from '@sgdocs/design-system';
import { queryClient } from '../common/hooks/queryClient';
import { QueryClientProvider } from 'react-query';
import { EWidgetsSgmDocs } from '../common/widget/enum';

const defaultLabels = {
  downloadInfo: 'Preview not available',
  downloadInfoDetails: 'This file type is not supported for preview. To visualize the content you can download it.',
  downloadButton: 'Download',
  downloadError: 'An unexpected error occurs.',
};

type ViewerProps = {
  sgmDocsBaseUrl: string;
  token?: string | null;
  hideDownloadButton?: string;
  additionalClassNames?: string;
};

type DocumentViewerProps = ViewerProps & {
  workspaceId: string;
  documentId: string;
  versionId?: string;
  mimeType: string;
  hideDownloadButton?: string;
  labels?: {
    downloadInfo?: string;
    downloadInfoDetails?: string;
    downloadButton?: string;
    downloadError?: string;
  };
};

type PermalinkDocumentViewProps = ViewerProps & {
  permalinkId: string;
  labels?: {
    downloadInfo?: string;
    downloadInfoDetails?: string;
    downloadButton?: string;
    downloadError?: string;
  };
};

export type IDocumentViewerComponentProps = DocumentViewerProps | PermalinkDocumentViewProps;

const DocsDocumentViewerComponent = ({
  sgmDocsBaseUrl,
  hideDownloadButton = 'false',
  labels,
  token = null,
  additionalClassNames = undefined,
  ...params
}: IDocumentViewerComponentProps) => {
  const { data: blobWithInfo, isLoading, isError } = useQueryFetchContent(params, { sgmDocsBaseUrl, token, component: EWidgetsSgmDocs.DOCUMENT_VIEWER });
  const labelsIntl = {
    ...defaultLabels,
    ...labels,
  };

  if (isLoading) {
    return (
      <div className="text-center m-2">
        <Spinner color="primary" />
      </div>
    );
  }

  if (!isError && !isLoading && blobWithInfo) {
    return (
        <DocumentViewer
          blob={blobWithInfo.contentBlob}
          contentType={blobWithInfo.contentType}
          contentName={blobWithInfo.documentName}
          hideDownloadButton={hideDownloadButton}
          labels={labelsIntl}
          additionalClassNames={additionalClassNames}
        />
    );
  } else {
    return <div className={'m-2'}>{labelsIntl.downloadError}</div>;
  }
};

export const SGMDocsDocumentViewer = ({
  sgmDocsBaseUrl,
  hideDownloadButton,
  labels,
  token,
  additionalClassNames,
  ...params
}: IDocumentViewerComponentProps) => {
  // @ts-ignore
  const { workspaceId, documentId, versionId, mimeType, permalinkId } = { ...params };
  return (
    <QueryClientProvider client={queryClient}>
      {permalinkId ? (
        <DocsDocumentViewerComponent
          sgmDocsBaseUrl={sgmDocsBaseUrl}
          hideDownloadButton={hideDownloadButton}
          labels={labels}
          token={token}
          additionalClassNames={additionalClassNames}
          permalinkId={permalinkId}
        />
      ) : (
        <DocsDocumentViewerComponent
          sgmDocsBaseUrl={sgmDocsBaseUrl}
          hideDownloadButton={hideDownloadButton}
          labels={labels}
          token={token}
          additionalClassNames={additionalClassNames}
          workspaceId={workspaceId}
          versionId={versionId}
          documentId={documentId}
          mimeType={mimeType}
        />
      )}
    </QueryClientProvider>
  );
};
